import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useUserPermission from './useUserPermission';
import { initializeLanguage, getStoredLanguageOrNull, isManualSwitch, clearManualSwitchFlag, SupportedLanguage } from '@/i18n';

/**
 * 语言初始化 Hook
 * 实现语言优先级：手动切换 > 接口返回 > 本地缓存 > 默认英文
 */
const useLanguageInitialization = () => {
  const { i18n } = useTranslation();
  const { language: userLanguage, isLanguageLoaded } = useUserPermission();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // 只有在 API 加载完成且还没初始化时执行
    if (isLanguageLoaded && !hasInitialized.current) {
      console.log('初始化语言设置:', userLanguage, '当前语言:', i18n.language);
      
      // 检查是否是手动切换后的刷新
      if (isManualSwitch()) {
        console.log('检测到手动切换标识，以手动设置为准');
        // 清除手动切换标识
        clearManualSwitchFlag();
        // 不执行任何语言设置，保持当前状态
        hasInitialized.current = true;
        return;
      }
      
      // 正常初始化流程：按优先级设置语言
      const storedLanguage = getStoredLanguageOrNull();
      let targetLanguage: SupportedLanguage;
      
      // 优先级：接口返回 > 本地缓存 > 默认英文
      if (userLanguage && userLanguage !== null) {
        targetLanguage = userLanguage;
        console.log('使用接口返回的语言:', targetLanguage);
      } else if (storedLanguage) {
        targetLanguage = storedLanguage;
        console.log('使用本地缓存的语言:', targetLanguage);
      } else {
        targetLanguage = 'en-US';
        console.log('使用默认语言:', targetLanguage);
      }
      
      // 如果目标语言和当前语言不同，则设置
      if (targetLanguage !== i18n.language) {
        console.log('设置语言到:', targetLanguage);
        initializeLanguage(targetLanguage);
      } else {
        console.log('当前语言已经是目标语言:', i18n.language);
      }
      
      // 标记已初始化
      hasInitialized.current = true;
    }
  }, [isLanguageLoaded, userLanguage]); // 监听 API 加载状态和 userLanguage

  return { currentLanguage: i18n.language, userLanguage };
};

export default useLanguageInitialization;
